import { useState } from 'react';
import type { RaptorJobDto } from '@/libs/api-rest/generated/model/raptorJobDto';
import { useResourcesFormState } from '@/raptorjob/presentation/hooks/useResourcesFormState';
import { useAutoPopulatedDispatcher } from './useAutoPopulatedDispatcher';
import { useERPJobInitialValues } from './useERPJobInitialValues';
import { useERPJobFormActions } from './useERPJobFormActions';
import { JOB_FORM_MODES, JOB_FORM_STEPS, type JobFormMode, type JobFormStepType } from '@/raptorjob/domain/constants/jobFormLabels';

export interface UseERPJobCreateModalParams {
  handleClose: () => void;
  openCreatedJob: (id: string) => void;
  mode?: JobFormMode;
  onSave?: (jobId: string) => void;
  jobId?: number;
  jobData?: RaptorJobDto;
  initialJobSiteId?: number;
  initialCustomerId?: number;
}

export const useERPJobCreateModal = ({
  handleClose,
  openCreatedJob,
  mode = JOB_FORM_MODES.CREATE,
  onSave,
  jobId,
  jobData,
  initialJobSiteId,
  initialCustomerId
}: UseERPJobCreateModalParams) => {
  const [step, setStep] = useState<JobFormStepType>(JOB_FORM_STEPS.JOB_INFO);
  const [hasUnavailableResources, setHasUnavailableResources] = useState(false);
  const [showResourceUnavailabilityWarningModal, setShowResourceUnavailabilityWarningModal] = useState(false);
  const [jobSiteSearchTerm, setJobSiteSearchTerm] = useState('');

  const {
    crewFields,
    setCrewFields,
    selectedEquipment,
    setSelectedEquipment,
    selectedClass,
    setSelectedClass,
    selectedType,
    setSelectedType,
    selectedSize,
    setSelectedSize,
    selectedMaxSize,
    setSelectedMaxSize,
    strict,
    setStrict,
    equipmentSearchTerm,
    setEquipmentSearchTerm
  } = useResourcesFormState();

  const { autoPopulatedDispatcher, autoPopulatedDispatcherId } = useAutoPopulatedDispatcher(mode);

  const {
    editData,
    initialValues,
    validationSchema,
    availableDispatchLocations,
    requiredFields,
    uiBehaviorRules,
    savedValues,
    setSavedValues,
    optionalFieldsVisibility,
    setFieldVisible,
    jobEndDateActive,
    setJobEndDateActive,
    poRequired,
    setPoRequired
  } = useERPJobInitialValues({
    mode,
    jobId,
    jobData,
    initialJobSiteId,
    initialCustomerId,
    autoPopulatedDispatcherId,
    step
  });

  const { handleSave, handleCloseWithPersist, trackFormValues } = useERPJobFormActions({
    mode,
    jobId,
    handleClose,
    openCreatedJob,
    onSave
  });

  return {
    step,
    setStep,
    hasUnavailableResources,
    setHasUnavailableResources,
    showResourceUnavailabilityWarningModal,
    setShowResourceUnavailabilityWarningModal,
    jobSiteSearchTerm,
    setJobSiteSearchTerm,
    crewFields,
    setCrewFields,
    selectedEquipment,
    setSelectedEquipment,
    selectedClass,
    setSelectedClass,
    selectedType,
    setSelectedType,
    selectedSize,
    setSelectedSize,
    selectedMaxSize,
    setSelectedMaxSize,
    strict,
    setStrict,
    equipmentSearchTerm,
    setEquipmentSearchTerm,
    initialValues,
    validationSchema,
    handleSave,
    handleCloseWithPersist,
    trackFormValues,
    editData,
    autoPopulatedDispatcher,
    availableDispatchLocations,
    requiredFields,
    uiBehaviorRules,
    savedValues,
    setSavedValues,
    optionalFieldsVisibility,
    setFieldVisible,
    jobEndDateActive,
    setJobEndDateActive,
    poRequired,
    setPoRequired
  };
};
